import { t } from 'i18next';
import { useCallback, useRef, useState } from 'react';
import { TXsollaSubscriptionOperation } from '../../../common/xsolla';
import { IXsollaCheckoutError, toXsollaCheckoutError } from '../../../common/xsollaCheckoutError';
import { TXsollaSubscriptionTier } from '../../../common/xsollaConfig';
import { IXsollaWidgetPaymentInfo } from './xsollaWidgetAdapter';
import { createCheckoutAttemptId, getGaClientContext } from './premiumXsollaAnalytics';
import { clearPendingCheckoutSelection, savePendingCheckoutSelection } from './pendingCheckoutSelection';

export type TXsollaCheckoutSelection = {
  tier: TXsollaSubscriptionTier;
  isAnnual: boolean;
  returnUrl?: string;
  operation?: TXsollaSubscriptionOperation;
  checkoutAttemptId: string;
  paymentExternalId?: string;
};

export type TCreateXsollaSubscriptionToken = (
  params: {
    tier: TXsollaSubscriptionTier;
    isAnnual: boolean;
    market?: string;
    language?: string;
    returnUrl?: string;
    operation?: TXsollaSubscriptionOperation;
    checkoutAttemptId?: string;
    gaClientId?: string;
    gaSessionId?: string;
  }
) => Promise<{ token: string; paymentExternalId?: string }>;

type TUseXsollaCheckoutSessionOptions = {
  createPaymentToken: TCreateXsollaSubscriptionToken;
  market?: string;
  language?: string;
  getReturnUrl?: (
    tier: TXsollaSubscriptionTier,
    isAnnual: boolean,
    operation?: TXsollaSubscriptionOperation
  ) => string | undefined;
  isAuthenticated?: boolean;
  onRequireAuthentication?: () => void;
  onCheckoutStart?: (selection: TXsollaCheckoutSelection) => void;
  onSuccess?: (paymentInfo: IXsollaWidgetPaymentInfo, selection: TXsollaCheckoutSelection) => void;
  onError?: (error: IXsollaCheckoutError) => void;
  onPending?: (paymentInfo: IXsollaWidgetPaymentInfo, selection: TXsollaCheckoutSelection) => void;
  onClose?: (closedSelection?: TXsollaCheckoutSelection) => void;
};

function getErrorStatusCode(error: any): number | undefined {
  return error?.statusCode || error?.status || error?.response?.statusCode || error?.response?.status;
}

function isAuthenticationRequiredError(error: unknown): boolean {
  const statusCode = getErrorStatusCode(error as any);
  if (statusCode === 401) {
    return true;
  }

  return error instanceof Error && error.message === 'Authentication required';
}

function getCheckoutStartError(error: unknown): IXsollaCheckoutError {
  const normalizedError = toXsollaCheckoutError(error) || { message: '' };
  const message = normalizedError.message || '';

  if (!message || message === 'Failed to create payment token' || message === 'Failed to start checkout') {
    return {
      ...normalizedError,
      message: t('xsolla:checkout-start-failed')
    };
  }

  if (message === 'No token received' || message === 'No token available') {
    return {
      ...normalizedError,
      message: t('xsolla:checkout-token-missing')
    };
  }

  return normalizedError;
}

export function useXsollaCheckoutSession(options: TUseXsollaCheckoutSessionOptions) {
  const {
    createPaymentToken,
    market,
    language,
    getReturnUrl,
    isAuthenticated = true,
    onRequireAuthentication,
    onCheckoutStart,
    onSuccess,
    onError,
    onPending,
    onClose
  } = options;
  const [isLoading, setIsLoading] = useState(false);
  const [checkoutToken, setCheckoutToken] = useState<string | null>(null);
  const [checkoutSelection, setCheckoutSelection] = useState<TXsollaCheckoutSelection | null>(null);
  const isStartingCheckoutRef = useRef(false);
  const isCheckoutActive = isLoading || !!checkoutToken || !!checkoutSelection;

  const startCheckout = useCallback(async (
    tier: TXsollaSubscriptionTier,
    isAnnual: boolean,
    operation?: TXsollaSubscriptionOperation
  ) => {
    if (!isAuthenticated) {
      // Keep the buyer's choice across the login round-trip; the rail resumes
      // checkout from this on its next authenticated mount.
      savePendingCheckoutSelection({ tier, isAnnual, operation });
      onRequireAuthentication?.();
      return;
    }

    if (isStartingCheckoutRef.current || checkoutToken || checkoutSelection) {
      return;
    }

    isStartingCheckoutRef.current = true;
    setIsLoading(true);

    try {
      const returnUrl = getReturnUrl?.(tier, isAnnual, operation);
      const checkoutAttemptId = createCheckoutAttemptId();
      const gaClientContext = getGaClientContext();
      const selection: TXsollaCheckoutSelection = {
        tier,
        isAnnual,
        checkoutAttemptId,
        ...(returnUrl ? { returnUrl } : {}),
        ...(operation ? { operation } : {})
      };

      setCheckoutSelection(selection);
      onCheckoutStart?.(selection);

      const result = await createPaymentToken({
        tier,
        isAnnual,
        market,
        language,
        checkoutAttemptId,
        ...gaClientContext,
        ...(returnUrl ? { returnUrl } : {}),
        ...(operation ? { operation } : {})
      });

      if (!result.token) {
        throw new Error('No token received');
      }

      setCheckoutSelection({
        ...selection,
        ...(result.paymentExternalId ? { paymentExternalId: result.paymentExternalId } : {})
      });
      setCheckoutToken(result.token);
    } catch (error: unknown) {
      setCheckoutSelection(null);
      if (isAuthenticationRequiredError(error)) {
        savePendingCheckoutSelection({ tier, isAnnual, operation });
        onRequireAuthentication?.();
        return;
      }

      console.error('Failed to create payment token:', error);
      onError?.(getCheckoutStartError(error));
    } finally {
      isStartingCheckoutRef.current = false;
      setIsLoading(false);
    }
  }, [checkoutSelection, checkoutToken, createPaymentToken, getReturnUrl, isAuthenticated, language, market, onCheckoutStart, onError, onRequireAuthentication]);

  const handleCheckoutSuccess = useCallback((paymentInfo: IXsollaWidgetPaymentInfo) => {
    // Terminal outcome: drop any persisted selection so a completed purchase can't
    // resurface a stale pending choice and auto-open checkout on a later mount.
    clearPendingCheckoutSelection();
    setCheckoutToken(null);
    if (checkoutSelection) {
      onSuccess?.(paymentInfo, checkoutSelection);
    }
    setCheckoutSelection(null);
  }, [checkoutSelection, onSuccess]);

  const handleCheckoutError = useCallback((error: string | IXsollaCheckoutError) => {
    setCheckoutToken(null);
    setCheckoutSelection(null);
    onError?.(typeof error === 'string' ? { message: error } : error);
  }, [onError]);

  const handleCheckoutPending = useCallback((paymentInfo: IXsollaWidgetPaymentInfo) => {
    // A settled-pending payment is also terminal for this session — clear the
    // persisted selection so it can't auto-resume later.
    clearPendingCheckoutSelection();
    if (checkoutSelection) {
      onPending?.(paymentInfo, checkoutSelection);
    }
  }, [checkoutSelection, onPending]);

  const handleCheckoutClose = useCallback(() => {
    const closedSelection = checkoutSelection;
    setCheckoutToken(null);
    setCheckoutSelection(null);
    onClose?.(closedSelection || undefined);
  }, [checkoutSelection, onClose]);

  return {
    isLoading,
    isCheckoutActive,
    checkoutToken,
    checkoutSelection,
    startCheckout,
    handleCheckoutSuccess,
    handleCheckoutError,
    handleCheckoutPending,
    handleCheckoutClose
  };
}
